import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * /authenticate-user — LevelUp SSO callback page.
 *
 * Flow:
 * 1. LevelUp redirects here after login: /authenticate-user?token=<one-time-token>
 * 2. We immediately strip the token from the URL (security + prevents browser refresh re-use)
 * 3. Call POST /api/auth/levelup with the token (one-time use — must NOT call twice)
 * 4. On success → redirect to /my-plans or /admin based on role
 * 5. On failure → show error, redirect to /login
 */
export default function AuthenticateUserPage() {
  const navigate = useNavigate();
  const { loginWithLevelUp, isAuthenticated } = useAuth();
  const [error, setError] = useState(null);
  // Guard: ensure token is consumed exactly once, even if React re-renders
  const tokenConsumed = useRef(false);

  useEffect(() => {
    if (tokenConsumed.current) return;

    // Read token from URL using native URLSearchParams (reliable in all contexts)
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    // Immediately strip the token from the browser URL bar (security + no re-use on refresh)
    if (token) {
      window.history.replaceState({}, "", window.location.pathname);
    }

    if (!token) {
      // No token in URL — redirect to login
      navigate("/login", { replace: true });
      return;
    }

    // Mark as consumed BEFORE the async call to prevent double-fire
    tokenConsumed.current = true;

    // Restore saved destination (persisted by LoginPage before the SSO redirect)
    const savedRedirect = localStorage.getItem("levelup_post_login_redirect") || "/";
    localStorage.removeItem("levelup_post_login_redirect");

    loginWithLevelUp(token)
      .then((userData) => {
        toast.success("Welcome to MyGoals!");
        // Route elevated SSO users (microsoft_manager etc.) to /admin
        const perms = userData?.permissions || [];
        if (userData?.role && userData.role !== "user" && perms.length > 0) {
          navigate("/admin", { replace: true });
        } else {
          navigate(savedRedirect, { replace: true });
        }
      })
      .catch(() => {
        setError("Your session expired. Please try signing in again.");
        toast.error("Your session expired. Please try signing in again.");
        // Redirect to login after a short delay
        setTimeout(() => navigate("/login", { replace: true }), 3000);
      });
  }, [loginWithLevelUp, navigate]);

  // If already authenticated (e.g. page refresh after successful login), redirect to homepage
  useEffect(() => {
    if (isAuthenticated) {
      const adminRole = localStorage.getItem("admin_role");
      if (adminRole && adminRole !== "user") {
        navigate("/admin", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [isAuthenticated, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFF8F3] flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-xl">!</span>
          </div>
          <p className="text-sm text-[#091F2C] font-medium mb-1">Authentication Failed</p>
          <p className="text-xs text-[#64748B]">{error}</p>
          <p className="text-xs text-[#94A3B8] mt-3">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8F3] flex items-center justify-center">
      <div className="text-center" data-testid="authenticate-user-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#194449] mx-auto mb-3" />
        <p className="text-sm text-[#64748B]">Signing you in...</p>
      </div>
    </div>
  );
}
